import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '@environments/environment';
import { ImageType } from '@core/websocket/websocket.models';
import { WebSocketService } from '@core/websocket/websocket.service';
import { WsProtobufService } from '@core/websocket/ws-protobuf.service';
import { RequestPath, ResponseStatus } from '@core/websocket/websocket.constants';

export interface UploadImageRequest {
  sessionId: string;
  imageType: ImageType;
  file: File;
}

export interface UploadImageResponse {
  id: string;
}

/**
 * Сервіс для завантаження та отримання фото водія.
 *
 * POST environment.imagesUrl        — завантаження фото (multipart/form-data)
 * GET  environment.imagesUrl/{id}   — отримання фото за id
 */
@Injectable({ providedIn: 'root' })
export class ImageService {
  private readonly http = inject(HttpClient);
  private readonly ws = inject(WebSocketService);
  private readonly proto = inject(WsProtobufService);

  private readonly baseUrl = environment.imagesUrl;

  /**
   * Завантажує фото на сервер.
   * @param request Параметри завантаження
   * @returns Об'єкт з id завантаженого зображення
   */
  upload(request: UploadImageRequest): Promise<UploadImageResponse> {
    const formData = new FormData();
    formData.append('session_id', request.sessionId);
    formData.append('image_type', request.imageType);
    formData.append('file', request.file, request.file.name);

    return firstValueFrom(
      this.http.post<UploadImageResponse>(this.baseUrl, formData),
    );
  }

  /**
   * Повертає абсолютний URL для отримання фото за id.
   */
  getImageUrl(id: string): string {
    return `${this.baseUrl}/${id}`;
  }

  /**
   * Видаляє фото за id через WebSocket.
   */
  async deleteImage(id: string): Promise<void> {
    const body = this.proto.encodeDeleteImageRequest(id);
    const response = await this.ws.sendRequest(RequestPath.DeleteImage, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`DeleteImage failed: code=${response.code}`);
    }
  }

  /**
   * Завантажує фото як Blob.
   */
  downloadBlob(id: string): Promise<Blob> {
    return firstValueFrom(
      this.http.get(this.getImageUrl(id), { responseType: 'blob' }),
    );
  }
}
